import {
  Box,
  Button,
  Collapsible,
  Heading,
  HStack,
  Icon,
  RadioCard,
  SegmentGroup,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"
import {
  EnergyCalculation,
  EnergyEstimate,
  getHeatDemandBreakdown,
  Home
} from "../../services/portfolioApi"
import {
  getDefaultEnergyEstimate,
  getElectricityChartData,
  getGasChartData,
  getTotalFromTotals
} from "../../utils/energyEstimateUtils"
import {
  getCalculationForOccupiers,
  getElectricityUsageData,
  getGasUsageData
} from "../../utils/energyUsageUtils"
import {
  buildHeatLossComponents,
  calculateTotalHeatLoss,
  getDeltaT,
  getDoorsLabel,
  getHeatLossScenarios,
  getLatestHeatLoss,
  getScenarioAdjustedLabel,
  getScenarioDescription,
  getScenarioLabel,
  getWindowsLabel
} from "../../utils/heatLossUtils"
import SummaryStatCard from "./SummaryStatCard"

interface EnergyDemandTabProps {
  heatLossCalculations: Array<any>
  home: Home
  selectedScenario: string
  onScenarioChange: (scenario: string) => void
  energyCalculations: EnergyCalculation[]
  energyEstimates: EnergyEstimate[]
  shouldUseEnergyEstimates: boolean
  copUsed?: number
  homeId: number
  isActive: boolean
}

const EnergyDemandTab = ({
  heatLossCalculations,
  home,
  selectedScenario,
  onScenarioChange,
  energyCalculations,
  energyEstimates,
  shouldUseEnergyEstimates,
  copUsed,
  homeId,
  isActive
}: EnergyDemandTabProps) => {
  const availableScenarios = getHeatLossScenarios(heatLossCalculations)

  const latestHeatLoss = getLatestHeatLoss(
    heatLossCalculations,
    selectedScenario
  )

  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const [showCalculations, setShowCalculations] = useState(false)
  const [selectedOccupiers, setSelectedOccupiers] = useState<number>(2)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [heatingType, setHeatingType] = useState<"gas" | "heatPump">("gas")

  const { data: heatDemandBreakdown, error: heatDemandBreakdownError } =
    useQuery({
      queryKey: ["heatDemandBreakdown", homeId],
      queryFn: () => getHeatDemandBreakdown(homeId),
      enabled: isActive && !!localStorage.getItem("token") && homeId > 0
    })

  // Calculate max value for each heating type across ALL scenarios (not just selected)
  // This ensures Y axis scale stays fixed when changing insulation scenarios
  const maxYAxisValue = useMemo(() => {
    if (!heatDemandBreakdown?.scenarios) return 0

    let maxGas = 0
    let maxHeatPump = 0

    // Check all scenarios to find the overall maximum for each heating type
    heatDemandBreakdown.scenarios.forEach((scenario: any) => {
      if (!scenario?.monthlyBreakdown) return

      scenario.monthlyBreakdown.forEach((item: any) => {
        // Gas boiler: increase by 15%
        const gasKwh = item.heatKwh * 1.15
        maxGas = Math.max(maxGas, gasKwh)

        // Heat pump: divide by COP
        if (item.cop && item.cop > 0) {
          const heatPumpKwh = item.heatKwh / item.cop
          maxHeatPump = Math.max(maxHeatPump, heatPumpKwh)
        }
      })
    })

    // Return the max for the current heating type, rounded up to nearest 1000
    const maxValue = heatingType === "heatPump" ? maxHeatPump : maxGas
    return Math.ceil(maxValue / 1000) * 1000
  }, [heatDemandBreakdown, heatingType])

  // Get monthly breakdown data for the selected scenario
  const monthlyChartData = useMemo(() => {
    if (!heatDemandBreakdown?.scenarios) return []

    const scenario = heatDemandBreakdown.scenarios.find(
      (s: any) => s.calculationScenario === selectedScenario
    )

    if (!scenario?.monthlyBreakdown) return []

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ]

    return scenario.monthlyBreakdown.map((item: any) => {
      let adjustedKwh = item.heatKwh

      if (heatingType === "gas") {
        adjustedKwh = adjustedKwh * 1.15
      }

      // If heat pump, divide by the month's COP attribute (more efficient)
      if (heatingType === "heatPump" && item.cop && item.cop > 0) {
        adjustedKwh = adjustedKwh / item.cop
      }

      return {
        month: monthNames[item.month - 1],
        monthNumber: item.month,
        heatKwh: Math.round(adjustedKwh * 10) / 10, // Round to 1 decimal
        cop: item.cop
      }
    })
  }, [heatDemandBreakdown, selectedScenario, heatingType])

  const heatLossComponents = latestHeatLoss
    ? buildHeatLossComponents(latestHeatLoss)
    : []

  const totalAnnualHeatDemandKwh = latestHeatLoss?.totalAnnualHeatDemandKwh || 0
  const deltaT = getDeltaT(latestHeatLoss)
  const totalHeatLoss = calculateTotalHeatLoss(heatLossComponents)

  const colorMap = {
    blue: "#3182ce",
    green: "#38a169",
    orange: "#dd6b20",
    purple: "#805ad5",
    red: "#e53e3e",
    teal: "#319795"
  } as const

  // Energy Usage logic
  const selectedCalculation = getCalculationForOccupiers(
    energyCalculations,
    selectedOccupiers
  )

  const selectedEstimate = getDefaultEnergyEstimate(energyEstimates)

  const { chartData: electricityChartData } = shouldUseEnergyEstimates
    ? getElectricityChartData(selectedEstimate!)
    : selectedCalculation
      ? getElectricityUsageData(selectedCalculation.breakdowns || [])
      : { chartData: [] }

  const { chartData: gasChartData, categoryTotals: gasTotals } =
    shouldUseEnergyEstimates && selectedEstimate
      ? getGasChartData(selectedEstimate)
      : selectedCalculation
        ? {
            ...getGasUsageData(selectedCalculation.breakdowns || []),
            categoryTotals: {}
          }
        : { chartData: [], categoryTotals: {} }

  const totalGasKwhNumber =
    shouldUseEnergyEstimates && selectedEstimate
      ? getTotalFromTotals(gasTotals)
      : selectedCalculation
        ? parseFloat(selectedCalculation.totalGasKwh)
        : 0

  const totalGasKwh = totalGasKwhNumber.toFixed(2)
  const hasValidCop = typeof copUsed === "number" && copUsed > 0
  const electrifiedGasKwh = hasValidCop
    ? (totalGasKwhNumber / (copUsed as number)).toFixed(0)
    : "--"
  const electrifiedCopLabel = hasValidCop
    ? (copUsed as number).toFixed(2)
    : "--"

  const categoryColors = {
    Kitchen: "#3182ce",
    Hygiene: "#38a169",
    Entertainment: "#dd6b20",
    Lighting: "#805ad5",
    Chores: "#e53e3e",
    "Always ON": "#9f7aea",
    "Always On": "#9f7aea",
    "Hot Water": "#d69e2e",
    Cooking: "#c05621"
  } as const

  const totalElectricityKwh =
    shouldUseEnergyEstimates && selectedEstimate
      ? selectedEstimate.totalElectricityKwh || "0"
      : selectedCalculation
        ? selectedCalculation.totalElectricityKwh
        : "0"

  // Calculate summary values
  const gasBoilerTotalKwh = totalAnnualHeatDemandKwh * 1.15
  const currentTotalGasKwh = gasBoilerTotalKwh + totalGasKwhNumber
  const currentTotalElectricityKwh = parseFloat(totalElectricityKwh)
  // Use COP 3.2 for electrified calculation as per subline
  const heatElectrifiedKwh =
    totalAnnualHeatDemandKwh > 0 ? totalAnnualHeatDemandKwh / 3.2 : 0
  const electrifiedGasKwhNumber =
    totalGasKwhNumber > 0 ? totalGasKwhNumber / 3.2 : 0
  const totalConsumptionElectrifiedKwh =
    heatElectrifiedKwh + currentTotalElectricityKwh + electrifiedGasKwhNumber

  return (
    <VStack gap={6} align="stretch">
      {/* Summary Cards */}
      <Box px={6} pt={6}>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <SummaryStatCard
            title="Estimated Gas usage 🔥"
            value={currentTotalGasKwh.toFixed(0)}
            subline="kWh per year"
            borderColor="gray.100"
          />
          <SummaryStatCard
            title="Estimated Electricity usage ⚡️"
            value={currentTotalElectricityKwh.toFixed(0)}
            subline="kWh per year"
            borderColor="gray.100"
          />
          <SummaryStatCard
            title="Estimated consumption if electrified 🔥→⚡️"
            value={totalConsumptionElectrifiedKwh.toFixed(0)}
            subline="kWh per year with an average COP of 3.2"
            borderColor="gray.100"
          />
        </SimpleGrid>
      </Box>

      {/* Main Heading */}
      <Heading size="lg" mt={6} mx={6}>
        Space Heating Demand
      </Heading>

      {/* Scenario Selector */}
      <Box bg="white" px={6} py={2} borderRadius="lg">
        <VStack gap={4} align="stretch">
          <Heading size="md">Insulation Scenario</Heading>
          <RadioCard.Root
            value={selectedScenario}
            onValueChange={(e) => onScenarioChange(e.value)}
          >
            <HStack gap={3}>
              {availableScenarios.map((scenario) => (
                <RadioCard.Item key={scenario} value={scenario} flex={1}>
                  <RadioCard.ItemHiddenInput />
                  <RadioCard.ItemControl>
                    <RadioCard.ItemContent>
                      <RadioCard.ItemText>
                        {getScenarioLabel(scenario)}
                      </RadioCard.ItemText>
                      <RadioCard.ItemDescription>
                        {getScenarioDescription(scenario)}
                      </RadioCard.ItemDescription>
                    </RadioCard.ItemContent>
                    <RadioCard.ItemIndicator />
                  </RadioCard.ItemControl>
                </RadioCard.Item>
              ))}
            </HStack>
          </RadioCard.Root>
        </VStack>
      </Box>

      {/* Heat Loss Breakdown */}
      <Box bg="white" px={6} py={2} borderRadius="lg">
        <VStack gap={4} align="stretch">
          <Heading size="md">Heat Loss Breakdown</Heading>

          <HStack gap={6} align="start">
            {/* Segmented Bar Chart */}
            <VStack gap={4} align="stretch" flex={1}>
              {/* Bar */}
              <Box
                position="relative"
                height="60px"
                width="100%"
                borderRadius="md"
                overflow="hidden"
                display="flex"
              >
                {heatLossComponents
                  .filter((c) => c.value > 0)
                  .map((component, index) => {
                    const percentage = (component.value / totalHeatLoss) * 100
                    const isSelected = selectedLabel === component.label
                    return (
                      <Box
                        key={component.label}
                        width={`${percentage}%`}
                        bg={colorMap[component.color as keyof typeof colorMap]}
                        opacity={selectedLabel && !isSelected ? 0.5 : 1}
                        cursor="pointer"
                        onClick={() =>
                          setSelectedLabel((prev) =>
                            prev === component.label ? null : component.label
                          )
                        }
                        transition="all 0.2s"
                        _hover={{ opacity: 0.8 }}
                        position="relative"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {percentage > 8 && (
                          <Text fontSize="xs" fontWeight="bold" color="white">
                            {percentage.toFixed(0)}%
                          </Text>
                        )}
                      </Box>
                    )
                  })}
              </Box>

              {/* Legend */}
              <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                {heatLossComponents
                  .filter((c) => c.value > 0)
                  .map((component) => {
                    const percentage = (component.value / totalHeatLoss) * 100
                    const isSelected = selectedLabel === component.label
                    return (
                      <HStack
                        key={component.label}
                        gap={2}
                        cursor="pointer"
                        onClick={() =>
                          setSelectedLabel((prev) =>
                            prev === component.label ? null : component.label
                          )
                        }
                        opacity={selectedLabel && !isSelected ? 0.5 : 1}
                        transition="all 0.2s"
                        _hover={{ opacity: 0.8 }}
                      >
                        <Box
                          width="16px"
                          height="16px"
                          borderRadius="sm"
                          bg={
                            colorMap[component.color as keyof typeof colorMap]
                          }
                        />
                        <VStack align="start" gap={0}>
                          <Text
                            fontSize="sm"
                            fontWeight={isSelected ? "bold" : "medium"}
                          >
                            {component.label}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {(component.value / 1000).toFixed(0)} kWh (
                            {percentage.toFixed(0)}%)
                          </Text>
                        </VStack>
                      </HStack>
                    )
                  })}
              </SimpleGrid>
            </VStack>

            {/* Total Heat Loss Info */}
            <VStack align="start" gap={3} minWidth="280px">
              <VStack align="start" gap={1}>
                <Text fontSize="2xl" fontWeight="bold">
                  {totalAnnualHeatDemandKwh.toFixed(0)} kWh/year
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Annual Heating Demand
                </Text>
                <Text fontSize="2xs" color="gray.500" mt={0}>
                  Last updated:{" "}
                  {latestHeatLoss?.updatedAt
                    ? new Date(latestHeatLoss.updatedAt).toLocaleDateString()
                    : "N/A"}
                </Text>
              </VStack>

              <VStack align="start" gap={2} mt={2}>
                <Text fontSize="sm" color="gray.700">
                  🔥 Gas boiler (85% efficiency):{" "}
                  <strong>
                    {(totalAnnualHeatDemandKwh * 1.15).toFixed(0)} kWh
                  </strong>
                </Text>
                <Text fontSize="sm" color="gray.700">
                  ⚡ Heat pump ({latestHeatLoss?.copUsed} COP):{" "}
                  <strong>
                    {Number(
                      (latestHeatLoss as any)?.totalElectrifiedHeatDemandKwh ??
                        0
                    ).toFixed(0)}{" "}
                    kWh
                  </strong>
                </Text>
              </VStack>
            </VStack>
          </HStack>
        </VStack>
      </Box>

      {/* Annual Heat Demand Breakdown */}
      {isActive &&
        heatDemandBreakdown &&
        !heatDemandBreakdownError &&
        monthlyChartData.length > 0 && (
          <Box bg="white" px={6} py={6} borderRadius="lg">
            <VStack gap={6} align="stretch">
              <HStack
                align="center"
                justify="space-between"
                flexWrap="wrap"
                gap={3}
              >
                <Heading size="md">Annual Heating Demand Breakdown</Heading>

                {/* Heating Type Toggle */}
                <SegmentGroup.Root
                  value={heatingType}
                  onValueChange={(e) =>
                    setHeatingType(e.value as "gas" | "heatPump")
                  }
                  display="inline-flex"
                  width="fit-content"
                  bg="gray.100"
                  borderRadius="md"
                  p="1"
                  boxShadow="sm"
                  gap="1"
                >
                  <SegmentGroup.Indicator
                    bg="white"
                    borderRadius="md"
                    boxShadow="sm"
                  />
                  <SegmentGroup.Item value="gas" px={4} py={2}>
                    <SegmentGroup.ItemText>🔥 Gas Boiler</SegmentGroup.ItemText>
                    <SegmentGroup.ItemHiddenInput />
                  </SegmentGroup.Item>
                  <SegmentGroup.Item value="heatPump" px={4} py={2}>
                    <SegmentGroup.ItemText>⚡ Heat Pump</SegmentGroup.ItemText>
                    <SegmentGroup.ItemHiddenInput />
                  </SegmentGroup.Item>
                </SegmentGroup.Root>
              </HStack>

              <Box width="100%" height="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthlyChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: "#4a5568", fontSize: 12 }}
                      stroke="#cbd5e0"
                    />
                    <YAxis
                      domain={[0, maxYAxisValue]}
                      tick={{ fill: "#4a5568", fontSize: 12 }}
                      stroke="#cbd5e0"
                      width={60}
                    />
                    <Tooltip
                      content={({ active, payload }: any) => {
                        if (!active || !payload || !payload.length) return null
                        const data = payload[0].payload
                        const value = data.heatKwh
                        const copValue = data.cop

                        return (
                          <Box
                            bg="white"
                            border="1px solid #e2e8f0"
                            borderRadius="6px"
                            p="8px 12px"
                          >
                            <Text fontWeight="bold" mb="4px">
                              {data.month}
                            </Text>
                            <Text>
                              {heatingType === "heatPump" && copValue
                                ? `${value.toFixed(1)} kWh (COP: ${Number(copValue).toFixed(2)})`
                                : `${value.toFixed(1)} kWh`}
                            </Text>
                          </Box>
                        )
                      }}
                    />
                    <Bar
                      dataKey="heatKwh"
                      fill="#3182ce"
                      radius={[4, 4, 0, 0]}
                      barSize={60}
                    >
                      <LabelList
                        dataKey="heatKwh"
                        position="top"
                        formatter={(value: any) =>
                          `${Number(value).toFixed(0)} kWh`
                        }
                        style={{
                          fill: "#4a5568",
                          fontSize: "11px",
                          fontWeight: "medium"
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </VStack>
          </Box>
        )}

      {/* Heat Loss Calculations - Collapsible */}
      {latestHeatLoss && (
        <Box bg="white" px={6} py={2} borderRadius="lg">
          <VStack gap={4} align="stretch">
            <Collapsible.Root
              open={showCalculations}
              onOpenChange={(details) => setShowCalculations(details.open)}
            >
              <VStack align="start" gap={2}>
                <Heading size="md">Heat Loss Calculations</Heading>
                <Collapsible.Trigger>
                  <Button variant="outline" size="sm">
                    <HStack gap={2}>
                      <Text>Show calculation details</Text>
                      <Icon
                        as="span"
                        transform={
                          showCalculations ? "rotate(180deg)" : "rotate(0deg)"
                        }
                        transition="transform 0.2s"
                        display="inline-block"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </Icon>
                    </HStack>
                  </Button>
                </Collapsible.Trigger>
              </VStack>
              <Collapsible.Content>
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                  mt={4}
                >
                  <VStack gap={6} align="stretch">
                    {/* U-Values and Surface Areas */}
                    <VStack gap={4} align="stretch">
                      <Heading size="sm">U-Values and Surface Areas</Heading>
                      <Box overflowX="auto">
                        <Box as="table" width="100%" borderCollapse="collapse">
                          <Box as="thead">
                            <Box as="tr">
                              <Box
                                as="th"
                                textAlign="left"
                                p={3}
                                fontWeight="bold"
                                fontSize="sm"
                                color="gray.600"
                              >
                                Component
                              </Box>
                              <Box
                                as="th"
                                textAlign="right"
                                p={3}
                                fontWeight="bold"
                                fontSize="sm"
                                color="gray.600"
                              >
                                U-Value (W/m²K)
                              </Box>
                              <Box
                                as="th"
                                textAlign="right"
                                p={3}
                                fontWeight="bold"
                                fontSize="sm"
                                color="gray.600"
                              >
                                Surface Area (m²)
                              </Box>
                              <Box
                                as="th"
                                textAlign="right"
                                p={3}
                                fontWeight="bold"
                                fontSize="sm"
                                color="gray.600"
                              >
                                W/m²
                              </Box>
                            </Box>
                          </Box>
                          <Box as="tbody">
                            <Box as="tr">
                              <Box as="td" p={3} fontWeight="medium">
                                Walls: {home.features.wallType},{" "}
                                {getScenarioAdjustedLabel(
                                  selectedScenario,
                                  home.features.wallInsulation === "Yes"
                                    ? "insulated"
                                    : "not insulated"
                                )}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.wallUvalue}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.externalWallSurface?.toFixed(1)}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.externalWallSurface &&
                                latestHeatLoss.wallUvalue
                                  ? (
                                      latestHeatLoss.wallUvalue *
                                      latestHeatLoss.externalWallSurface *
                                      deltaT
                                    ).toFixed(1)
                                  : "N/A"}
                              </Box>
                            </Box>
                            <Box as="tr">
                              <Box as="td" p={3} fontWeight="medium">
                                Roof: {home.features.roofType},{" "}
                                {getScenarioAdjustedLabel(
                                  selectedScenario,
                                  home.features.roofInsulated === "Yes"
                                    ? "insulated"
                                    : "not insulated"
                                )}
                                {selectedScenario === "default" &&
                                  home.features.roofInsulationThickness !==
                                    undefined &&
                                  home.features.roofInsulationThickness !==
                                    null &&
                                  ` • ${home.features.roofInsulationThickness}mm`}
                                {selectedScenario === "insulated" && ` • 300mm`}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.roofUvalue}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.roofSurface}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.roofSurface &&
                                latestHeatLoss.roofUvalue
                                  ? (
                                      latestHeatLoss.roofUvalue *
                                      latestHeatLoss.roofSurface *
                                      deltaT
                                    ).toFixed(1)
                                  : "N/A"}
                              </Box>
                            </Box>
                            <Box as="tr">
                              <Box as="td" p={3} fontWeight="medium">
                                Floor: {home.features.floorType},{" "}
                                {getScenarioAdjustedLabel(
                                  selectedScenario,
                                  home.features.floorInsulated === "Yes"
                                    ? "insulated"
                                    : "not insulated"
                                )}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.floorUvalue}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {home.features.floorArea}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {home.features.floorArea &&
                                latestHeatLoss.floorUvalue
                                  ? (
                                      latestHeatLoss.floorUvalue *
                                      home.features.floorArea *
                                      deltaT
                                    ).toFixed(1)
                                  : "N/A"}
                              </Box>
                            </Box>
                            <Box as="tr">
                              <Box as="td" p={3} fontWeight="medium">
                                Windows:{" "}
                                {getWindowsLabel(selectedScenario, home)}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.windowUvalue}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.windowsSurface?.toFixed(1)}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.windowsSurface &&
                                latestHeatLoss.windowUvalue
                                  ? (
                                      latestHeatLoss.windowUvalue *
                                      latestHeatLoss.windowsSurface *
                                      deltaT
                                    ).toFixed(1)
                                  : "N/A"}
                              </Box>
                            </Box>
                            <Box as="tr">
                              <Box as="td" p={3} fontWeight="medium">
                                Doors: {getDoorsLabel()}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.doorUvalue}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.doorsSurface}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss.doorsSurface &&
                                latestHeatLoss.doorUvalue
                                  ? (
                                      latestHeatLoss.doorUvalue *
                                      latestHeatLoss.doorsSurface *
                                      deltaT
                                    ).toFixed(1)
                                  : "N/A"}
                              </Box>
                            </Box>
                            <Box as="tr" borderTop="1px solid #e2e8f0">
                              <Box as="td" p={3} fontWeight="medium">
                                Ventilation: {home.features.ventilationType}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                ACH50: {latestHeatLoss.airChangeRate}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                m<sup>3</sup>:{" "}
                                {latestHeatLoss.airVolume
                                  ? Number(latestHeatLoss.airVolume).toFixed(1)
                                  : "N/A"}
                              </Box>
                              <Box
                                as="td"
                                p={3}
                                textAlign="right"
                                fontFamily="mono"
                              >
                                {latestHeatLoss?.ventilationHeatLossWh != null
                                  ? Number(
                                      latestHeatLoss.ventilationHeatLossWh
                                    ).toFixed(1)
                                  : "N/A"}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </VStack>

                    {/* Calculation Variables */}
                    <VStack gap={4} align="stretch">
                      <Heading size="sm">Calculation Variables</Heading>
                      <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" color="gray.600">
                            Internal Design Temperature
                          </Text>
                          <Text fontSize="2xl" fontWeight="bold">
                            {latestHeatLoss?.internalDesignTemperature || 20}°C
                          </Text>
                        </VStack>
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" color="gray.600">
                            External Design Temperature
                          </Text>
                          <Text fontSize="2xl" fontWeight="bold">
                            {latestHeatLoss?.externalDesignTemperature || -2}°C
                          </Text>
                        </VStack>
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" color="gray.600">
                            Baseline Heating Hours per Year
                          </Text>
                          <Text fontSize="2xl" fontWeight="bold">
                            {latestHeatLoss?.heatingHoursBaseline || 1215} hours
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            (7.5 hours/day for 6 months)
                          </Text>
                        </VStack>
                      </SimpleGrid>
                    </VStack>
                  </VStack>
                </Box>
              </Collapsible.Content>
            </Collapsible.Root>
          </VStack>
        </Box>
      )}

      <Box borderTop="1px solid" borderColor="gray.200" my={4} />

      {/* Energy Usage Section */}
      <Heading size="lg" my={0} mx={6}>
        Other Energy Demand
      </Heading>

      {/* Energy Usage Content */}
      {shouldUseEnergyEstimates ? (
        // Energy Estimates Tab Content
        selectedEstimate ? (
          <>
            {/* Electricity Usage Breakdown */}
            <Box bg="white" px={6} py={2} borderRadius="lg">
              <VStack gap={4} align="stretch">
                <Heading size="sm">Electricity Usage Breakdown</Heading>
                <Text fontSize="sm" color="gray.600">
                  Annual electricity consumption for all household activities
                  except space heating.
                </Text>

                <HStack gap={6} align="start">
                  {/* Segmented Bar Chart */}
                  <VStack gap={4} align="stretch" flex={1}>
                    {/* Bar */}
                    <Box
                      position="relative"
                      height="60px"
                      width="100%"
                      borderRadius="md"
                      overflow="hidden"
                      display="flex"
                      bg="gray.100"
                    >
                      {electricityChartData
                        .filter((c) => c.value > 0)
                        .map((component) => {
                          const total = parseFloat(totalElectricityKwh)
                          const percentage =
                            total > 0 ? (component.value / total) * 100 : 0
                          const isSelected = selectedCategory === component.name
                          return (
                            <Box
                              key={component.name}
                              width={`${percentage}%`}
                              bg={
                                categoryColors[
                                  component.name as keyof typeof categoryColors
                                ] || "#718096"
                              }
                              opacity={
                                selectedCategory && !isSelected ? 0.5 : 1
                              }
                              cursor="pointer"
                              onClick={() =>
                                setSelectedCategory((prev) =>
                                  prev === component.name
                                    ? null
                                    : component.name
                                )
                              }
                              transition="all 0.2s"
                              _hover={{ opacity: 0.8 }}
                              position="relative"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              minHeight="100%"
                            >
                              {percentage > 8 && (
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="white"
                                  zIndex={2}
                                >
                                  {percentage.toFixed(0)}%
                                </Text>
                              )}
                            </Box>
                          )
                        })}
                    </Box>

                    {/* Legend */}
                    <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                      {electricityChartData
                        .filter((c) => c.value > 0)
                        .map((component) => {
                          const total = parseFloat(totalElectricityKwh)
                          const percentage =
                            total > 0 ? (component.value / total) * 100 : 0
                          const isSelected = selectedCategory === component.name
                          return (
                            <HStack
                              key={component.name}
                              gap={2}
                              cursor="pointer"
                              onClick={() =>
                                setSelectedCategory((prev) =>
                                  prev === component.name
                                    ? null
                                    : component.name
                                )
                              }
                              opacity={
                                selectedCategory && !isSelected ? 0.5 : 1
                              }
                              transition="all 0.2s"
                              _hover={{ opacity: 0.8 }}
                            >
                              <Box
                                width="16px"
                                height="16px"
                                borderRadius="sm"
                                bg={
                                  categoryColors[
                                    component.name as keyof typeof categoryColors
                                  ] || "#718096"
                                }
                              />
                              <VStack align="start" gap={0}>
                                <Text
                                  fontSize="sm"
                                  fontWeight={isSelected ? "bold" : "medium"}
                                >
                                  {component.name}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {component.value.toFixed(0)} kWh (
                                  {percentage.toFixed(0)}%)
                                </Text>
                              </VStack>
                            </HStack>
                          )
                        })}
                    </SimpleGrid>
                  </VStack>

                  {/* Total Energy Info */}
                  <VStack align="start" gap={3} minWidth="280px">
                    <VStack align="start" gap={1}>
                      <Text fontSize="2xl" fontWeight="bold">
                        {parseFloat(totalElectricityKwh).toFixed(0)} kWh/year
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Total Electricity Usage
                      </Text>
                    </VStack>

                    <VStack align="start" gap={2} mt={2}>
                      <Text fontSize="sm" color="gray.700">
                        💡 Electricity breakdown by category
                      </Text>
                    </VStack>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Gas Usage Breakdown */}
            <Box bg="white" p={6} borderRadius="lg">
              <VStack gap={4} align="stretch">
                <Heading size="sm">Gas Usage Breakdown</Heading>
                <Text fontSize="sm" color="gray.600">
                  Annual gas consumption for household activities like cooking
                  and hot water, excluding space heating.
                </Text>

                <HStack gap={6} align="start">
                  {/* Segmented Bar Chart */}
                  <VStack gap={4} align="stretch" flex={1}>
                    {/* Bar */}
                    <Box
                      position="relative"
                      height="60px"
                      width="100%"
                      borderRadius="md"
                      overflow="hidden"
                      display="flex"
                      bg="gray.100"
                    >
                      {gasChartData
                        .filter((c) => c.value > 0)
                        .map((component) => {
                          const total = parseFloat(totalGasKwh)
                          const percentage =
                            total > 0 ? (component.value / total) * 100 : 0
                          const isSelected = selectedCategory === component.name
                          return (
                            <Box
                              key={component.name}
                              width={`${percentage}%`}
                              bg={
                                categoryColors[
                                  component.name as keyof typeof categoryColors
                                ] || "#718096"
                              }
                              opacity={
                                selectedCategory && !isSelected ? 0.5 : 1
                              }
                              cursor="pointer"
                              onClick={() =>
                                setSelectedCategory((prev) =>
                                  prev === component.name
                                    ? null
                                    : component.name
                                )
                              }
                              transition="all 0.2s"
                              _hover={{ opacity: 0.8 }}
                              position="relative"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              minHeight="100%"
                            >
                              {percentage > 8 && (
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="white"
                                  zIndex={2}
                                >
                                  {percentage.toFixed(0)}%
                                </Text>
                              )}
                            </Box>
                          )
                        })}
                    </Box>

                    {/* Legend */}
                    <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                      {gasChartData
                        .filter((c) => c.value > 0)
                        .map((component) => {
                          const total = parseFloat(totalGasKwh)
                          const percentage =
                            total > 0 ? (component.value / total) * 100 : 0
                          const isSelected = selectedCategory === component.name
                          return (
                            <HStack
                              key={component.name}
                              gap={2}
                              cursor="pointer"
                              onClick={() =>
                                setSelectedCategory((prev) =>
                                  prev === component.name
                                    ? null
                                    : component.name
                                )
                              }
                              opacity={
                                selectedCategory && !isSelected ? 0.5 : 1
                              }
                              transition="all 0.2s"
                              _hover={{ opacity: 0.8 }}
                            >
                              <Box
                                width="16px"
                                height="16px"
                                borderRadius="sm"
                                bg={
                                  categoryColors[
                                    component.name as keyof typeof categoryColors
                                  ] || "#718096"
                                }
                              />
                              <VStack align="start" gap={0}>
                                <Text
                                  fontSize="sm"
                                  fontWeight={isSelected ? "bold" : "medium"}
                                >
                                  {component.name}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {component.value.toFixed(0)} kWh (
                                  {percentage.toFixed(0)}%)
                                </Text>
                              </VStack>
                            </HStack>
                          )
                        })}
                    </SimpleGrid>
                  </VStack>

                  {/* Total Gas Info */}
                  <VStack align="start" gap={3} minWidth="280px">
                    <VStack align="start" gap={1}>
                      <Text fontSize="2xl" fontWeight="bold">
                        {totalGasKwhNumber.toFixed(0)} kWh/year
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Total Gas Usage
                      </Text>
                    </VStack>

                    <VStack align="start" gap={2} mt={2}>
                      <Text fontSize="sm" color="gray.700">
                        🔥 Gas breakdown by category
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        ⚡ Electrified ({electrifiedCopLabel} COP):{" "}
                        <strong>{electrifiedGasKwh} kWh</strong>
                      </Text>
                    </VStack>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </>
        ) : (
          <Box bg="white" p={6} borderRadius="lg">
            <VStack gap={4} align="stretch">
              <Heading size="sm">Energy Usage Analysis</Heading>
              <Text color="gray.600">
                Energy usage data is not available for this home yet.
              </Text>
            </VStack>
          </Box>
        )
      ) : // Energy Usage Tab Content
      selectedCalculation ? (
        <>
          {/* Occupier Selector */}
          <Box bg="white" p={6} borderRadius="lg">
            <VStack gap={4} align="stretch">
              <Heading size="sm">Number of Occupiers</Heading>
              <RadioCard.Root
                value={selectedOccupiers.toString()}
                onValueChange={(e) => setSelectedOccupiers(parseInt(e.value))}
              >
                <HStack gap={3}>
                  {[1, 2, 3].map((num) => (
                    <RadioCard.Item key={num} value={num.toString()} flex={1}>
                      <RadioCard.ItemHiddenInput />
                      <RadioCard.ItemControl>
                        <RadioCard.ItemContent>
                          <RadioCard.ItemText>
                            {num} {num === 1 ? "Person" : "People"}
                          </RadioCard.ItemText>
                        </RadioCard.ItemContent>
                        <RadioCard.ItemIndicator />
                      </RadioCard.ItemControl>
                    </RadioCard.Item>
                  ))}
                </HStack>
              </RadioCard.Root>
            </VStack>
          </Box>

          {/* Electricity Usage Breakdown */}
          <Box bg="white" p={6} borderRadius="lg">
            <VStack gap={4} align="stretch">
              <Heading size="sm">Electricity Usage Breakdown</Heading>
              <Text fontSize="sm" color="gray.600">
                Annual electricity consumption for all household activities
                except space heating.
              </Text>

              <HStack gap={6} align="start">
                {/* Segmented Bar Chart */}
                <VStack gap={4} align="stretch" flex={1}>
                  {/* Bar */}
                  <Box
                    position="relative"
                    height="60px"
                    width="100%"
                    borderRadius="md"
                    overflow="hidden"
                    display="flex"
                    bg="gray.100"
                  >
                    {electricityChartData
                      .filter((c) => c.value > 0)
                      .map((component, index) => {
                        const total = parseFloat(
                          selectedCalculation.totalElectricityKwh
                        )
                        const percentage =
                          total > 0 ? (component.value / total) * 100 : 0
                        const isSelected = selectedCategory === component.name
                        return (
                          <Box
                            key={component.name}
                            width={`${percentage}%`}
                            bg={
                              categoryColors[
                                component.name as keyof typeof categoryColors
                              ] || "#718096"
                            }
                            opacity={selectedCategory && !isSelected ? 0.5 : 1}
                            cursor="pointer"
                            onClick={() =>
                              setSelectedCategory((prev) =>
                                prev === component.name ? null : component.name
                              )
                            }
                            transition="all 0.2s"
                            _hover={{ opacity: 0.8 }}
                            position="relative"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="100%"
                          >
                            {percentage > 8 && (
                              <Text
                                fontSize="xs"
                                fontWeight="bold"
                                color="white"
                                zIndex={2}
                              >
                                {percentage.toFixed(0)}%
                              </Text>
                            )}
                          </Box>
                        )
                      })}
                  </Box>

                  {/* Legend */}
                  <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                    {electricityChartData
                      .filter((c) => c.value > 0)
                      .map((component) => {
                        const total = parseFloat(
                          selectedCalculation.totalElectricityKwh
                        )
                        const percentage =
                          total > 0 ? (component.value / total) * 100 : 0
                        const isSelected = selectedCategory === component.name
                        return (
                          <HStack
                            key={component.name}
                            gap={2}
                            cursor="pointer"
                            onClick={() =>
                              setSelectedCategory((prev) =>
                                prev === component.name ? null : component.name
                              )
                            }
                            opacity={selectedCategory && !isSelected ? 0.5 : 1}
                            transition="all 0.2s"
                            _hover={{ opacity: 0.8 }}
                          >
                            <Box
                              width="16px"
                              height="16px"
                              borderRadius="sm"
                              bg={
                                categoryColors[
                                  component.name as keyof typeof categoryColors
                                ] || "#718096"
                              }
                            />
                            <VStack align="start" gap={0}>
                              <Text
                                fontSize="sm"
                                fontWeight={isSelected ? "bold" : "medium"}
                              >
                                {component.name}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {component.value.toFixed(0)} kWh (
                                {percentage.toFixed(0)}%)
                              </Text>
                            </VStack>
                          </HStack>
                        )
                      })}
                  </SimpleGrid>
                </VStack>

                {/* Total Energy Info */}
                <VStack align="start" gap={3} minWidth="280px">
                  <VStack align="start" gap={1}>
                    <Text fontSize="2xl" fontWeight="bold">
                      {parseFloat(
                        selectedCalculation.totalElectricityKwh
                      ).toFixed(0)}{" "}
                      kWh/year
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Total Electricity Usage
                    </Text>
                    <Text fontSize="2xs" color="gray.500" mt={0}>
                      Last updated:{" "}
                      {selectedCalculation.updatedAt
                        ? new Date(
                            selectedCalculation.updatedAt
                          ).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </VStack>

                  <VStack align="start" gap={2} mt={2}>
                    <Text fontSize="sm" color="gray.700">
                      💡 Electricity breakdown by category
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      🔧 Based on {selectedOccupiers}{" "}
                      {selectedOccupiers === 1 ? "occupant" : "occupants"}
                    </Text>
                  </VStack>
                </VStack>
              </HStack>
            </VStack>
          </Box>

          {/* Gas Usage Breakdown */}
          <Box bg="white" p={6} borderRadius="lg">
            <VStack gap={4} align="stretch">
              <Heading size="sm">Gas Usage Breakdown</Heading>
              <Text fontSize="sm" color="gray.600">
                Annual gas consumption for household activities like cooking and
                hot water, excluding space heating.
              </Text>

              <HStack gap={6} align="start">
                {/* Segmented Bar Chart */}
                <VStack gap={4} align="stretch" flex={1}>
                  {/* Bar */}
                  <Box
                    position="relative"
                    height="60px"
                    width="100%"
                    borderRadius="md"
                    overflow="hidden"
                    display="flex"
                    bg="gray.100"
                  >
                    {gasChartData
                      .filter((c) => c.value > 0)
                      .map((component, index) => {
                        const total = parseFloat(
                          selectedCalculation.totalGasKwh
                        )
                        const percentage =
                          total > 0 ? (component.value / total) * 100 : 0
                        const isSelected = selectedCategory === component.name
                        return (
                          <Box
                            key={component.name}
                            width={`${percentage}%`}
                            bg={
                              categoryColors[
                                component.name as keyof typeof categoryColors
                              ] || "#718096"
                            }
                            opacity={selectedCategory && !isSelected ? 0.5 : 1}
                            cursor="pointer"
                            onClick={() =>
                              setSelectedCategory((prev) =>
                                prev === component.name ? null : component.name
                              )
                            }
                            transition="all 0.2s"
                            _hover={{ opacity: 0.8 }}
                            position="relative"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="100%"
                          >
                            {percentage > 8 && (
                              <Text
                                fontSize="xs"
                                fontWeight="bold"
                                color="white"
                                zIndex={2}
                              >
                                {percentage.toFixed(0)}%
                              </Text>
                            )}
                          </Box>
                        )
                      })}
                  </Box>

                  {/* Legend */}
                  <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                    {gasChartData
                      .filter((c) => c.value > 0)
                      .map((component) => {
                        const total = parseFloat(
                          selectedCalculation.totalGasKwh
                        )
                        const percentage =
                          total > 0 ? (component.value / total) * 100 : 0
                        const isSelected = selectedCategory === component.name
                        return (
                          <HStack
                            key={component.name}
                            gap={2}
                            cursor="pointer"
                            onClick={() =>
                              setSelectedCategory((prev) =>
                                prev === component.name ? null : component.name
                              )
                            }
                            opacity={selectedCategory && !isSelected ? 0.5 : 1}
                            transition="all 0.2s"
                            _hover={{ opacity: 0.8 }}
                          >
                            <Box
                              width="16px"
                              height="16px"
                              borderRadius="sm"
                              bg={
                                categoryColors[
                                  component.name as keyof typeof categoryColors
                                ] || "#718096"
                              }
                            />
                            <VStack align="start" gap={0}>
                              <Text
                                fontSize="sm"
                                fontWeight={isSelected ? "bold" : "medium"}
                              >
                                {component.name}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {component.value.toFixed(0)} kWh (
                                {percentage.toFixed(0)}%)
                              </Text>
                            </VStack>
                          </HStack>
                        )
                      })}
                  </SimpleGrid>
                </VStack>

                {/* Total Gas Info */}
                <VStack align="start" gap={3} minWidth="280px">
                  <VStack align="start" gap={1}>
                    <Text fontSize="2xl" fontWeight="bold">
                      {parseFloat(selectedCalculation.totalGasKwh).toFixed(0)}{" "}
                      kWh/year
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Total Gas Usage
                    </Text>
                    <Text fontSize="2xs" color="gray.500" mt={0}>
                      Last updated:{" "}
                      {selectedCalculation.updatedAt
                        ? new Date(
                            selectedCalculation.updatedAt
                          ).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </VStack>

                  <VStack align="start" gap={2} mt={2}>
                    <Text fontSize="sm" color="gray.700">
                      🔥 Gas breakdown by category
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      🔧 Based on {selectedOccupiers}{" "}
                      {selectedOccupiers === 1 ? "occupant" : "occupants"}
                    </Text>
                  </VStack>
                </VStack>
              </HStack>
            </VStack>
          </Box>
        </>
      ) : (
        <Box bg="white" p={6} borderRadius="lg">
          <VStack gap={4} align="stretch">
            <Heading size="sm">Energy Usage Analysis</Heading>
            <Text color="gray.600">
              Energy usage data is not available for this home yet.
            </Text>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}

export default EnergyDemandTab
